import {
  Alert,
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { RegisterErrorResponse, useRegister } from "../api/auth";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useSnackbar } from "notistack";
import Head from "next/head";

import { messages } from "./../locales/messages/auth";
import { Link } from "../components/Link";
import { AuthLayout } from "../components/AuthLayout";
import { getMetaTitle } from "../utils/misc";
import { sendEvent, setUserId } from "../utils/analytics";

interface Form {
  admin: {
    name: string;
    email: string;
    password: string;
    passwordConfirmation: string;
  };

  company: {
    name: string;
  };
}

type ErrorKey =
  | "admin.password"
  | "admin.name"
  | "admin.email"
  | "admin.passwordConfirmation"
  | "company.name";

export default function Register() {
  const intl = useIntl();

  const register = useRegister();

  const { enqueueSnackbar } = useSnackbar();

  const form = useForm<Form>({
    defaultValues: {
      admin: {
        name: "",
        email: "",
        password: "",
        passwordConfirmation: "",
      },

      company: {
        name: undefined,
      },
    },
  });

  const { getValues, setError, handleSubmit, control } = form;

  const confirmationValidation = (confirmation: string) => {
    const password = getValues("admin.password");

    if (password && confirmation && password !== confirmation) {
      return intl.formatMessage(messages.register.errors.passwordConfirmation);
    }

    return true;
  };

  const setErrors = (errors: RegisterErrorResponse["errorFields"]) => {
    Object.entries(errors).forEach(([context, errorFields]) => {
      Object.entries(errorFields).forEach(([field, messages]) => {
        setError(`${context}.${field}` as ErrorKey, {
          type: "manual",
          message: messages.join(", "),
        });
      });
    });
  };

  const onSubmit: SubmitHandler<Form> = (data) => {
    register.mutate(data, {
      onSuccess: (response) => {
        form.reset();
        setUserId(String(response.id));
        sendEvent("sign_up", {
          method: "email",
        });
      },

      onError: (error) => {
        const errorFields = error.errorFields;

        if (errorFields) {
          setErrors(errorFields);
        } else {
          enqueueSnackbar(intl.formatMessage(messages.register.notif.error), {
            variant: "error",
          });
        }
      },
    });
  };

  const Form = () => {
    return (
      <form onSubmit={handleSubmit(onSubmit)} id="register-form">
        <Controller
          name="admin.name"
          control={control}
          rules={{
            required: intl.formatMessage(messages.register.errors.required),
          }}
          render={({
            field: { onChange, value },
            fieldState: { invalid, error },
          }) => (
            <TextField
              autoFocus
              sx={{ mt: 3 }}
              value={value}
              onChange={onChange}
              error={invalid}
              fullWidth
              label={intl.formatMessage(messages.register.form.labels.name)}
              InputLabelProps={{
                required: true,
              }}
              helperText={error?.message}
            />
          )}
        />

        <Controller
          name="admin.email"
          control={control}
          rules={{
            required: intl.formatMessage(messages.register.errors.required),
          }}
          render={({
            field: { onChange, value },
            fieldState: { invalid, error },
          }) => (
            <TextField
              sx={{ mt: 3 }}
              value={value}
              onChange={onChange}
              error={invalid}
              type="email"
              fullWidth
              label={intl.formatMessage(messages.register.form.labels.email)}
              InputLabelProps={{
                required: true,
              }}
              helperText={error?.message}
            />
          )}
        />

        <Controller
          name="company.name"
          control={control}
          render={({
            field: { onChange, value },
            fieldState: { invalid, error },
          }) => (
            <TextField
              sx={{ mt: 3 }}
              value={value}
              onChange={onChange}
              error={invalid}
              fullWidth
              label={intl.formatMessage(
                messages.register.form.labels.companyName
              )}
              helperText={error?.message}
            />
          )}
        />

        <Controller
          name="admin.password"
          control={control}
          rules={{
            required: intl.formatMessage(messages.register.errors.required),
          }}
          render={({
            field: { onChange, value },
            fieldState: { invalid, error },
          }) => (
            <TextField
              type="password"
              sx={{ mt: 3 }}
              value={value}
              onChange={onChange}
              error={invalid}
              fullWidth
              label={intl.formatMessage(messages.register.form.labels.password)}
              InputLabelProps={{
                required: true,
              }}
              helperText={error?.message}
            />
          )}
        />

        <Controller
          name="admin.passwordConfirmation"
          control={control}
          rules={{
            required: intl.formatMessage(messages.register.errors.required),
            validate: confirmationValidation,
          }}
          render={({
            field: { onChange, value },
            fieldState: { invalid, error },
          }) => (
            <TextField
              type="password"
              sx={{ mt: 3 }}
              value={value}
              onChange={onChange}
              error={invalid}
              fullWidth
              label={intl.formatMessage(
                messages.register.form.labels.passwordConfirmation
              )}
              InputLabelProps={{
                required: true,
              }}
              helperText={error?.message}
            />
          )}
        />

        <Box sx={{ my: 2 }}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            <FormattedMessage
              defaultMessage="Zakładając konto, akceptujesz nasz {termsLink} oraz {privacyPolicyLink}."
              id="JoCaGa"
              values={{
                termsLink: (
                  <Link href="https://coachmate.app/terms" target="blank">
                    <FormattedMessage defaultMessage="Regulamin" id="be19C3" />
                  </Link>
                ),
                privacyPolicyLink: (
                  <Link
                    href="https://coachmate.app/privacy-policy"
                    target="blank"
                  >
                    <FormattedMessage
                      defaultMessage="Politykę Prywatności"
                      id="Z3/bOq"
                    />
                  </Link>
                ),
              }}
            />
          </Typography>
        </Box>

        <Button
          disabled={register.isLoading}
          fullWidth
          variant="contained"
          type="submit"
          sx={{ mb: 2 }}
        >
          <FormattedMessage defaultMessage="Zarejestruj się" id="ARRFrr" />
        </Button>

        <Divider>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            <FormattedMessage defaultMessage="lub" id="nVMaPJ" />
          </Typography>
        </Divider>

        <Link href="/login" underline="none">
          <Button
            fullWidth
            sx={{ my: 2 }}
            variant="text"
            disabled={register.isLoading}
          >
            <FormattedMessage defaultMessage="Zaloguj się" id="kYs7ww" />
          </Button>
        </Link>
      </form>
    );
  };

  const Success = () => {
    return (
      <Box sx={{ minWidth: "396px" }}>
        <Alert sx={{ my: 1 }} severity="success">
          {intl.formatMessage(messages.register.notif.success)}
        </Alert>

        <Link href="/login" underline="none">
          <Button fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
            {intl.formatMessage(messages.login.buttons.signIn)}
          </Button>
        </Link>
      </Box>
    );
  };

  return (
    <>
      <Head>
        <title>
          {getMetaTitle(
            intl.formatMessage({
              defaultMessage: "Zarejestruj się",
              id: "ARRFrr",
            })
          )}
        </title>
      </Head>
      <AuthLayout>
        <Avatar sx={{ m: 1, bgcolor: "primary.main" }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          {intl.formatMessage(messages.register.title)}
        </Typography>
        <Box sx={{ mt: 1 }}>
          {register.isSuccess && <Success />}

          {!register.isSuccess && <Form />}
        </Box>
      </AuthLayout>
    </>
  );
}

Register.noLayout = true;
